import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '@/lib/api';
import { 
  Search,
  Filter,
  Edit,
  Trash2,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  UserCog,
  RotateCcw,
  Flame,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatDistanceToNow } from 'date-fns';
import { startImpersonation } from '@/lib/tokenStorage';
import AutoPurgeStatus from './AutoPurgeStatus';
import HealthChip from './HealthChip';
import UserEngagementDrawer from './UserEngagementDrawer';
import { SoftDeleteDialog, PurgePermanentlyDialog, BulkActionDialog } from './SubscribersDialogs';

const SubscribersList = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [purgingUser, setPurgingUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // iter163 — bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null);  // 'delete' | 'restore' | 'purge'

  // iter165 — user activity drawer
  const [drawerUserId, setDrawerUserId] = useState(null);

  const itemsPerPage = 20;
  const isDeletedView = statusFilter === 'deleted';

  const fetchSubscribers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
      };
      
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      const response = await api.get('/admin/subscribers', { params });
      setSubscribers(response.data.subscribers);
      setTotalCount(response.data.total);
      setSelectedIds(new Set());  // clear selection whenever the list changes
    } catch (error) {
      toast.error('Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, itemsPerPage]);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  const handleEdit = async (userData) => {
    try {
      setActionLoading(true);
      const response = await api.patch(`/admin/subscribers/${editingUser.id}`, userData);
      
      // Update the subscriber in the list
      setSubscribers(prev => prev.map(sub => 
        sub.id === editingUser.id ? response.data : sub
      ));
      
      setEditingUser(null);
      toast.success('Subscriber updated successfully');
    } catch (error) {
      toast.error('Failed to update subscriber');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSoftDelete = async () => {
    try {
      setActionLoading(true);
      await api.delete(`/admin/subscribers/${deletingUser.id}`);
      await fetchSubscribers();
      setDeletingUser(null);
      toast.success('Subscriber moved to Deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete subscriber');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestore = async (user) => {
    try {
      await api.post(`/admin/subscribers/${user.id}/restore`);
      await fetchSubscribers();
      toast.success(`Restored ${user.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore subscriber');
    }
  };

  const handlePurge = async () => {
    try {
      setActionLoading(true);
      await api.post(`/admin/subscribers/${purgingUser.id}/purge`);
      await fetchSubscribers();
      setPurgingUser(null);
      toast.success('Subscriber permanently purged');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to purge subscriber');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkConfirm = async () => {
    if (!bulkAction || selectedIds.size === 0) return;
    try {
      setActionLoading(true);
      const res = await api.post('/admin/subscribers/bulk', {
        ids: Array.from(selectedIds),
        action: bulkAction,
      });
      const { succeeded, failed } = res.data;
      if (failed > 0) {
        toast.warning(`Bulk ${bulkAction}: ${succeeded} succeeded, ${failed} failed`);
      } else {
        toast.success(`Bulk ${bulkAction}: ${succeeded} subscriber(s) processed`);
      }
      await fetchSubscribers();
      setBulkAction(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Bulk ${bulkAction} failed`);
    } finally {
      setActionLoading(false);
    }
  };

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    const visibleIds = filteredSubscribers.map(s => s.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) {
        visibleIds.forEach(id => next.delete(id));
      } else {
        visibleIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const getStatusBadge = (subscriber) => {
    if (subscriber.deleted_at) {
      return <Badge className="bg-rose-900/30 text-rose-200" variant="secondary">Deleted</Badge>;
    }
    const status = subscriber.subscription_status;
    const statusConfig = {
      trial: { color: 'bg-orange-900/30 text-orange-200', label: 'Trial' },
      active: { color: 'bg-emerald-900/30 text-emerald-200', label: 'Active' },
      expired: { color: 'bg-yellow-900/30 text-yellow-200', label: 'Expired' },
      cancelled: { color: 'bg-amber-900/30 text-amber-200', label: 'Cancelled' },
    };
    const config = statusConfig[status] || { color: 'bg-[#131C3A] text-slate-100', label: status };
    return (
      <Badge className={config.color} variant="secondary">
        {config.label}
      </Badge>
    );
  };

  const filteredSubscribers = useMemo(() => subscribers.filter(subscriber => 
    subscriber.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    subscriber.email?.toLowerCase().includes(searchTerm.toLowerCase())
  ), [subscribers, searchTerm]);

  const allVisibleSelected = filteredSubscribers.length > 0
    && filteredSubscribers.every(s => selectedIds.has(s.id));

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  if (loading) {
    return (
      <div className="p-8">
        <div className="mb-8">
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={`subscriber-skeleton-${i}`} className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div>
                      <Skeleton className="h-4 w-32 mb-1" />
                      <Skeleton className="h-3 w-48" />
                    </div>
                  </div>
                  <Skeleton className="h-6 w-16" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-100 mb-2" data-testid="subscribers-title">
          Subscribers Management
        </h1>
        <p className="text-slate-300">
          Manage user subscriptions, view details, and perform administrative actions.
        </p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="subscribers-search"
                />
              </div>
            </div>
            
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-48" data-testid="status-filter">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="deleted" data-testid="filter-option-deleted">Deleted (recycle bin)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* iter164 — Auto-Purge status widget only in Deleted view */}
      {isDeletedView && <AutoPurgeStatus onPurged={fetchSubscribers} />}

      {/* Bulk actions toolbar (iter163) */}
      {selectedIds.size > 0 && (
        <Card className="mb-4 border-blue-500/40 bg-blue-950/20" data-testid="bulk-actions-bar">
          <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
            <div className="flex items-center gap-3">
              <Badge className="bg-blue-900/40 text-blue-100 border-blue-500/40" data-testid="bulk-selected-count">
                {selectedIds.size} selected
              </Badge>
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="text-xs text-slate-300 underline hover:text-slate-100"
                data-testid="bulk-clear-selection"
              >
                Clear selection
              </button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {isDeletedView ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setBulkAction('restore')}
                    data-testid="bulk-restore-btn"
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Bulk restore
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setBulkAction('purge')}
                    data-testid="bulk-purge-btn"
                  >
                    <Flame className="mr-2 h-4 w-4" />
                    Bulk purge permanently
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkAction('delete')}
                  className="border-amber-500/40 text-amber-300 hover:bg-amber-900/20"
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Bulk soft-delete
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Subscribers Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-slate-100">
            Subscribers ({totalCount})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={allVisibleSelected}
                    onCheckedChange={toggleAll}
                    aria-label="Select all visible"
                    data-testid="select-all-checkbox"
                  />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Health</TableHead>
                <TableHead>Last active</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>{isDeletedView ? 'Deleted' : 'Joined'}</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSubscribers.map((subscriber) => (
                <TableRow key={subscriber.id} data-testid={`subscriber-row-${subscriber.id}`}>
                  <TableCell>
                    <Checkbox
                      checked={selectedIds.has(subscriber.id)}
                      onCheckedChange={() => toggleOne(subscriber.id)}
                      aria-label={`Select ${subscriber.email}`}
                      data-testid={`row-checkbox-${subscriber.id}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium">
                    {subscriber.full_name || 'N/A'}
                  </TableCell>
                  <TableCell>{subscriber.email}</TableCell>
                  <TableCell>{getStatusBadge(subscriber)}</TableCell>
                  <TableCell>
                    <button
                      onClick={(e) => { e.stopPropagation(); setDrawerUserId(subscriber.id); }}
                      className="hover:opacity-80 focus:outline-none"
                      data-testid={`open-activity-${subscriber.id}`}
                      title="View activity"
                    >
                      <HealthChip status={subscriber.health_status || 'never_activated'} />
                    </button>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-slate-300" data-testid={`last-active-${subscriber.id}`}>
                      {subscriber.last_active_at
                        ? formatDistanceToNow(new Date(subscriber.last_active_at), { addSuffix: true })
                        : <span className="text-slate-500">never</span>}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="capitalize">
                      {subscriber.subscription_plan || 'Free'}
                    </span>
                  </TableCell>
                  <TableCell>
                    {isDeletedView && subscriber.deleted_at
                      ? format(new Date(subscriber.deleted_at), 'MMM d, yyyy')
                      : subscriber.created_at
                        ? format(new Date(subscriber.created_at), 'MMM d, yyyy')
                        : 'N/A'
                    }
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          data-testid={`subscriber-actions-${subscriber.id}`}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {isDeletedView ? (
                          <>
                            <DropdownMenuItem
                              onClick={() => handleRestore(subscriber)}
                              data-testid={`restore-subscriber-${subscriber.id}`}
                            >
                              <RotateCcw className="mr-2 h-4 w-4" />
                              Restore
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setPurgingUser(subscriber)}
                              className="text-rose-400 hover:text-rose-300"
                              data-testid={`purge-subscriber-${subscriber.id}`}
                            >
                              <Flame className="mr-2 h-4 w-4" />
                              Purge permanently
                            </DropdownMenuItem>
                          </>
                        ) : (
                          <>
                            <DropdownMenuItem
                              onClick={() => setEditingUser(subscriber)}
                              data-testid={`edit-subscriber-${subscriber.id}`}
                            >
                              <Edit className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={async () => {
                                try {
                                  const r = await api.post(`/admin/impersonate/${subscriber.id}`);
                                  const ok = startImpersonation(r.data.token);
                                  if (!ok) {
                                    toast.error('Invalid impersonation token');
                                    return;
                                  }
                                  toast.success(`Now viewing as ${r.data.target_user.email}`);
                                  setTimeout(() => { window.location.href = '/dashboard'; }, 600);
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || 'Could not start impersonation');
                                }
                              }}
                              data-testid={`impersonate-subscriber-${subscriber.id}`}
                            >
                              <UserCog className="mr-2 h-4 w-4" />
                              Login as user
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setDeletingUser(subscriber)}
                              className="text-amber-400 hover:text-amber-300"
                              data-testid={`delete-subscriber-${subscriber.id}`}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete (soft)
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {filteredSubscribers.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No subscribers found matching your criteria.
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-sm text-slate-300">
                Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} subscribers
              </p>
              
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  data-testid="prev-page"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                
                <span className="text-sm text-slate-300">
                  Page {currentPage} of {totalPages}
                </span>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  data-testid="next-page"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent data-testid="edit-subscriber-dialog">
          <DialogHeader>
            <DialogTitle>Edit Subscriber</DialogTitle>
            <DialogDescription>
              Update subscription status and plan for {editingUser?.full_name || editingUser?.email}
            </DialogDescription>
          </DialogHeader>
          
          {editingUser && (
            <EditSubscriberForm 
              user={editingUser}
              onSubmit={handleEdit}
              loading={actionLoading}
              onCancel={() => setEditingUser(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete + Purge + Bulk confirm dialogs (extracted iter167) */}
      <SoftDeleteDialog
        user={deletingUser}
        loading={actionLoading}
        onCancel={() => setDeletingUser(null)}
        onConfirm={handleSoftDelete}
      />
      <PurgePermanentlyDialog
        user={purgingUser}
        loading={actionLoading}
        onCancel={() => setPurgingUser(null)}
        onConfirm={handlePurge}
      />
      <BulkActionDialog
        action={bulkAction}
        count={selectedIds.size}
        loading={actionLoading}
        onCancel={() => setBulkAction(null)}
        onConfirm={handleBulkConfirm}
      />

      {/* iter165 — Per-user engagement drill-down drawer */}
      <UserEngagementDrawer
        userId={drawerUserId}
        open={!!drawerUserId}
        onOpenChange={(v) => !v && setDrawerUserId(null)}
      />
    </div>
  );
};

// Edit Subscriber Form Component
//
// iter133 — subscription plan dropdown now surfaces the FULL canonical 4-tier
// catalog from `backend/constants/pricing.py` (starter, pro, business,
// enterprise). Previously it only offered `free/pro/enterprise` which
// (a) missed Starter + Business entirely and (b) exposed `free` — a legacy
// alias that `normalize_subscription_plan()` collapses into `starter`.
//
// Legacy values on existing user records (`free`, `monthly`, `team`, `custom`,
// `demo`, `trial`, `admin`) are normalised into a canonical slug here so the
// Select renders the correct starting option instead of a blank chip.
const _normalisePlan = (plan) => {
  const p = (plan || '').toLowerCase();
  if (['starter', 'free', 'demo', 'trial'].includes(p)) return 'starter';
  if (['pro', 'monthly'].includes(p)) return 'pro';
  if (['business', 'team'].includes(p)) return 'business';
  if (['enterprise', 'custom'].includes(p)) return 'enterprise';
  return 'starter';
};

const EditSubscriberForm = ({ user, onSubmit, loading, onCancel }) => {
  const [formData, setFormData] = useState({
    full_name: user.full_name || '',
    subscription_status: user.subscription_status || 'trial',
    subscription_plan: _normalisePlan(user.subscription_plan),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="full_name">Full Name</Label>
        <Input
          id="full_name"
          value={formData.full_name}
          onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
          placeholder="Enter full name"
          data-testid="edit-full-name"
        />
      </div>
      
      <div>
        <Label htmlFor="subscription_status">Subscription Status</Label>
        <Select
          value={formData.subscription_status}
          onValueChange={(value) => setFormData(prev => ({ ...prev, subscription_status: value }))}
        >
          <SelectTrigger data-testid="edit-subscription-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="trial">Trial</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label htmlFor="subscription_plan">Subscription Plan</Label>
        <Select
          value={formData.subscription_plan}
          onValueChange={(value) => setFormData(prev => ({ ...prev, subscription_plan: value }))}
        >
          <SelectTrigger data-testid="edit-subscription-plan">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="starter" data-testid="edit-plan-option-starter">
              Starter — $49/mo · 1,500 AI calls · 1 seat
            </SelectItem>
            <SelectItem value="pro" data-testid="edit-plan-option-pro">
              Pro — $99/mo · 5,000 AI calls · 2 seats
            </SelectItem>
            <SelectItem value="business" data-testid="edit-plan-option-business">
              Business — $299/mo · 10,000 AI calls · 5 seats
            </SelectItem>
            <SelectItem value="enterprise" data-testid="edit-plan-option-enterprise">
              Enterprise — Custom · Custom seats
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
          data-testid="cancel-edit"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={loading}
          className="bg-[#DC2626] hover:bg-[#DC2626]/90"
          data-testid="save-subscriber"
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogFooter>
    </form>
  );
};

export default SubscribersList;